import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AttachMoney as MoneyIcon,
  Payment as PaymentIcon,
  ShoppingCart as CartIcon,
  Inventory as InventoryIcon,
  Tune as TuneIcon,
  MoreHoriz as OtherIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { roundMoney } from '../../../utils/money';
import { sectionPhaseSx } from '../shared/salesStep2Phase.styles';
import { formatCurrency } from '../../../utils/formatters/money';
import { useSalesStep2 } from './SalesStep2.hooks';
import { PaymentMethod } from '../../../api/models/PaymentMethod';
import { FlagSwitch } from '../../../components/ui';
import ProductImagePreview, { ProductImageData } from '../shared/ProductImagePreview';
import SalesStepHint from '../shared/SalesStepHint/SalesStepHint';
import LinePricingToolbar from '../shared/LinePricingToolbar/LinePricingToolbar';
import SalesLinePricingCard from '../shared/SalesLinePricingCard/SalesLinePricingCard';
import RemoteOriginChip from '../shared/RemoteOriginChip';
import PaymentAmountDialog from '../shared/PaymentAmountDialog';
import OrderChargesSection from './OrderChargesSection';
import { cartHasRemoteItems, isRemoteCartItem } from '../../../utils/sales/mixed-sale.utils';
import * as S from './SalesStep2.styles';

const getProductImageData = (product: ProductImageData): ProductImageData => ({
  thumbnail: product.thumbnail ?? null,
  thumbnailUrl: product.thumbnailUrl ?? null,
  imagePath: product.imagePath ?? null,
});

const SalesStep2: React.FC = () => {
  const {
    state,
    orderSummary,
    flowPhase,
    configEditable,
    productsEditable,
    productsSectionActive,
    hasCommercialConfig,
    chargesSectionVisible,
    chargesEditable,
    paymentConfigVisible,
    paymentConfigEditable,
    paymentMethodsVisible,
    paymentMethodsEditable,
    paymentsLocked,
    commitPricingConfig,
    confirmProductsSection,
    confirmChargesSection,
    confirmPaymentConfigSection,
    unlockConfigForEdit,
    unlockProductsForEdit,
    unlockChargesForEdit,
    unlockPaymentConfigForEdit,
    unlockPaymentMethodsForEdit,
    canConfirmConfigSection,
    canConfirmProductsSection,
    hasInvalidLinePricing,
    pricingDraft,
    maxUniformDiscountAmount,
    orderMarkupHasNoCatalogBase,
    canProceed,
    paymentMethodOptions,
    otherPaymentMethods,
    showOtherMethodsDialog,
    showPaymentAmountDialog,
    pendingPaymentMethod,
    editingPaymentId,
    payments,
    handleMarkupToggle,
    handleIndividualMarkupToggle,
    handleMarkupTypeChange,
    handleMarkupValueChange,
    handleProductMarkupChange,
    handleDiscountToggle,
    handleIndividualDiscountToggle,
    handleDiscountTypeChange,
    handleDiscountValueChange,
    handleProductDiscountChange,
    handlePaymentMethodClick,
    handleOtherMethodSelect,
    handleOpenOtherMethods,
    handleCloseOtherMethods,
    handleAddPayment,
    handleRemovePayment,
    handleUpdatePaymentAmount,
    handleEditPayment,
    handleClosePaymentAmountDialog,
    handlePartialPaymentToggle,
    handleMultiplePaymentsToggle,
    handleNextStep,
    previousStep,
    formatAdjustmentDisplay,
    getPaymentMethodName,
    getTotalPayments,
    getRemainingBalance,
    orderCharges,
    chargeCategories,
    addOrderCharge,
    updateOrderCharge,
    removeOrderCharge,
  } = useSalesStep2();

  const showProductsSection = hasCommercialConfig || flowPhase === 2;

  return (
    <S.MainContainer>
      <S.LeftColumn>
        {/* Sub-etapa 1: Configuración comercial */}
        <Box sx={{ mb: 3 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TuneIcon color="primary" />
                  Configuración comercial
                </Typography>
                {flowPhase > 1 && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={unlockConfigForEdit}
                    sx={{ cursor: 'pointer' }}
                  >
                    Modificar configuración
                  </Link>
                )}
              </Box>

              <Box sx={sectionPhaseSx(flowPhase === 1)}>
                <LinePricingToolbar
                  editable={configEditable}
                  markupEnabled={pricingDraft.markupEnabled}
                  allowIndividualMarkups={pricingDraft.allowIndividualMarkups}
                  discountEnabled={pricingDraft.discountEnabled}
                  allowIndividualDiscounts={pricingDraft.allowIndividualDiscounts}
                  markupType={pricingDraft.markupType}
                  markupValue={pricingDraft.markupValue}
                  discountType={pricingDraft.discountType}
                  discountValue={pricingDraft.discountValue}
                  maxUniformDiscountAmount={maxUniformDiscountAmount}
                  orderMarkupHasNoCatalogBase={orderMarkupHasNoCatalogBase}
                  onMarkupToggle={handleMarkupToggle}
                  onIndividualMarkupToggle={handleIndividualMarkupToggle}
                  onMarkupTypeChange={handleMarkupTypeChange}
                  onMarkupValueChange={handleMarkupValueChange}
                  onDiscountToggle={handleDiscountToggle}
                  onIndividualDiscountToggle={handleIndividualDiscountToggle}
                  onDiscountTypeChange={handleDiscountTypeChange}
                  onDiscountValueChange={handleDiscountValueChange}
                />

                {flowPhase === 1 && (
                  <S.SubStepGhostContinueButton
                    fullWidth
                    onClick={commitPricingConfig}
                    disabled={!canConfirmConfigSection}
                    startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                    sx={{ mt: 1.5 }}
                  >
                    {hasCommercialConfig ? 'Aplicar a piezas' : 'Continuar a cargos'}
                  </S.SubStepGhostContinueButton>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Sub-etapa 2: Piezas con precios aplicados (solo si hay config comercial) */}
        {showProductsSection && (
          <Box sx={{ mb: 3 }}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon color="primary" />
                    Piezas ({state.selectedProducts.length})
                  </Typography>
                  {flowPhase > 2 && productsSectionActive && (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={unlockProductsForEdit}
                      sx={{ cursor: 'pointer' }}
                    >
                      Modificar piezas
                    </Link>
                  )}
                </Box>

                <Box sx={sectionPhaseSx(productsSectionActive || flowPhase === 2)}>
                  {flowPhase < 2 && (
                    <SalesStepHint tone="info">
                      Confirma la configuración comercial para ver el detalle por pieza
                    </SalesStepHint>
                  )}

                  {flowPhase >= 2 &&
                    productsSectionActive &&
                    state.selectedProducts.map((product) => (
                      <SalesLinePricingCard
                        key={product.warehouseInventoryProduct.id}
                        product={product}
                        sellingLocationId={state.selectedLocation?.id}
                        editable={productsEditable}
                        showMarkupControls={pricingDraft.markupEnabled}
                        showIndividualMarkupControls={pricingDraft.allowIndividualMarkups}
                        showDiscountControls={state.discountConfig.enabled}
                        showIndividualDiscountControls={
                          state.discountConfig.allowIndividualDiscounts
                        }
                        onMarkupChange={handleProductMarkupChange}
                        onDiscountChange={handleProductDiscountChange}
                        formatAdjustmentDisplay={formatAdjustmentDisplay}
                      />
                    ))}

                  {flowPhase === 2 && hasInvalidLinePricing && (
                    <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
                      Corrige los precios inválidos antes de continuar.
                    </Alert>
                  )}

                  {flowPhase === 2 && (
                    <S.SubStepGhostContinueButton
                      fullWidth
                      onClick={confirmProductsSection}
                      disabled={!canConfirmProductsSection}
                      startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                      sx={{ mt: 1.5 }}
                    >
                      Continuar a cargos
                    </S.SubStepGhostContinueButton>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        <OrderChargesSection
          charges={orderCharges}
          categories={chargeCategories}
          unlocked={chargesSectionVisible}
          editable={chargesEditable}
          showModifyLink={flowPhase > 3}
          onModify={unlockChargesForEdit}
          showContinueCta={flowPhase === 3}
          onContinue={confirmChargesSection}
          lockedHint={
            hasCommercialConfig
              ? 'Confirma las piezas para gestionar cargos adicionales'
              : 'Confirma la configuración comercial para gestionar cargos adicionales'
          }
          onAddCharge={addOrderCharge}
          onUpdateCharge={updateOrderCharge}
          onRemoveCharge={removeOrderCharge}
        />

        {/* Sub-etapa 4: Configuración de pago */}
        <Box sx={{ mb: 3 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'text.primary',
                  }}
                >
                  <PaymentIcon color="primary" />
                  Configuración de pago
                </Typography>
                {flowPhase > 4 && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={unlockPaymentConfigForEdit}
                    sx={{ cursor: 'pointer' }}
                  >
                    Modificar configuración
                  </Link>
                )}
              </Box>

              <Box sx={sectionPhaseSx(paymentConfigVisible)}>
                {!paymentConfigVisible && (
                  <SalesStepHint tone="info">
                    Confirma los cargos para configurar el pago
                  </SalesStepHint>
                )}

                {paymentConfigVisible && (
                  <>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <S.PaymentToggleBox isActive={state.paymentConfig.allowPartialPayment}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <MoneyIcon
                                color={
                                  state.paymentConfig.allowPartialPayment ? 'success' : 'action'
                                }
                                sx={{ fontSize: 16 }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                color={
                                  state.paymentConfig.allowPartialPayment
                                    ? 'success.main'
                                    : 'text.primary'
                                }
                                sx={{ fontSize: '0.75rem' }}
                              >
                                Pago Parcial
                              </Typography>
                            </Box>
                            <FlagSwitch
                              checked={state.paymentConfig.allowPartialPayment}
                              onChange={(e, checked) => handlePartialPaymentToggle(checked)}
                              disabled={!paymentConfigEditable}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}
                          >
                            {state.paymentConfig.allowPartialPayment
                              ? 'Pago parcial permitido'
                              : 'Pago total obligatorio'}
                          </Typography>
                        </S.PaymentToggleBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <S.PaymentToggleBox isActive={state.paymentConfig.allowMultiplePayments}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PaymentIcon
                                color={
                                  state.paymentConfig.allowMultiplePayments ? 'success' : 'action'
                                }
                                sx={{ fontSize: 16 }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                color={
                                  state.paymentConfig.allowMultiplePayments
                                    ? 'success.main'
                                    : 'text.primary'
                                }
                                sx={{ fontSize: '0.75rem' }}
                              >
                                Múltiples pagos
                              </Typography>
                            </Box>
                            <FlagSwitch
                              checked={state.paymentConfig.allowMultiplePayments}
                              onChange={(e, checked) => handleMultiplePaymentsToggle(checked)}
                              disabled={!paymentConfigEditable}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}
                          >
                            {state.paymentConfig.allowMultiplePayments
                              ? 'Permite agregar varios pagos.'
                              : 'Solo se permite un pago.'}
                          </Typography>
                        </S.PaymentToggleBox>
                      </Grid>
                    </Grid>

                    {flowPhase === 4 && (
                      <S.SubStepGhostContinueButton
                        fullWidth
                        onClick={confirmPaymentConfigSection}
                        startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                        sx={{ mt: 1.5 }}
                      >
                        Continuar a métodos de pago
                      </S.SubStepGhostContinueButton>
                    )}
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Sub-etapa 5: Métodos de pago */}
        <Box sx={{ mb: 3 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'text.primary',
                  }}
                >
                  <PaymentIcon color="primary" />
                  Métodos de Pago
                </Typography>
                {paymentsLocked && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={unlockPaymentMethodsForEdit}
                    sx={{ cursor: 'pointer' }}
                  >
                    Modificar métodos
                  </Link>
                )}
              </Box>

              <Box sx={sectionPhaseSx(paymentMethodsVisible)}>
                {!paymentMethodsVisible && (
                  <SalesStepHint tone="info">
                    Confirma la configuración de pago para registrar métodos
                  </SalesStepHint>
                )}

                {paymentMethodsVisible && orderSummary.grandTotal <= 0 && (
                  <SalesStepHint tone="info">
                    Total en 0: no se requiere pago. Puedes continuar con la venta.
                  </SalesStepHint>
                )}

                {paymentMethodsVisible && (
                  <>
                    <Grid container spacing={1.5} sx={{ mb: 3 }}>
                      {paymentMethodOptions.map((method) => {
                        const IconComponent = method.icon;
                        return (
                          <Grid item xs={3} key={method.id}>
                            <S.PaymentMethodButton
                              fullWidth
                              variant="outlined"
                              onClick={() => handlePaymentMethodClick(method.id)}
                              disabled={
                                !paymentMethodsEditable ||
                                (!state.paymentConfig.allowMultiplePayments && payments.length >= 1)
                              }
                              isSelected={false}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: 0.25,
                                }}
                              >
                                <S.PaymentMethodIconBox isSelected={false}>
                                  <IconComponent
                                    sx={{
                                      fontSize: 16,
                                      color: 'text.secondary',
                                    }}
                                  />
                                </S.PaymentMethodIconBox>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {method.name}
                                </Typography>
                              </Box>
                            </S.PaymentMethodButton>
                          </Grid>
                        );
                      })}
                      <Grid item xs={3}>
                        <S.PaymentMethodButton
                          fullWidth
                          variant="outlined"
                          onClick={handleOpenOtherMethods}
                          disabled={
                            !paymentMethodsEditable ||
                            otherPaymentMethods.length === 0 ||
                            (!state.paymentConfig.allowMultiplePayments && payments.length >= 1)
                          }
                          isSelected={false}
                          sx={{
                            opacity: otherPaymentMethods.length === 0 ? 0.5 : 1,
                            cursor: otherPaymentMethods.length === 0 ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 0.25,
                            }}
                          >
                            <S.PaymentMethodIconBox isSelected={false}>
                              <OtherIcon
                                sx={{
                                  fontSize: 16,
                                  color: 'text.secondary',
                                }}
                              />
                            </S.PaymentMethodIconBox>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                textAlign: 'center',
                                lineHeight: 1.2,
                              }}
                            >
                              Otro
                            </Typography>
                          </Box>
                        </S.PaymentMethodButton>
                      </Grid>
                    </Grid>

                    {payments.length === 0 ? (
                      <SalesStepHint tone="success">
                        No hay pagos agregados. Seleccione un método de pago para agregar un pago.
                      </SalesStepHint>
                    ) : (
                      <Box sx={{ mb: 3 }}>
                        {payments.map((payment) => {
                          const isCash = payment.method === 'CASH';
                          const IconComponent =
                            paymentMethodOptions.find((m) => m.id === payment.method)?.icon ??
                            PaymentIcon;
                          return (
                            <Card
                              key={payment.id}
                              sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}
                            >
                              <CardContent>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  <Box sx={{ flex: 1 }}>
                                    <Box
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                                    >
                                      <IconComponent fontSize="small" color="primary" />
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {getPaymentMethodName(payment.method)}
                                      </Typography>
                                    </Box>
                                    <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>
                                      {formatCurrency(payment.amount)}
                                    </Typography>
                                    {isCash &&
                                      payment.cashReceived != null &&
                                      payment.cashReceived > payment.amount && (
                                        <Typography variant="body2" color="text.secondary">
                                          Recibido: {formatCurrency(payment.cashReceived)}
                                          {' - '}
                                          Cambio:{' '}
                                          {formatCurrency(payment.cashReceived - payment.amount)}
                                        </Typography>
                                      )}
                                    {payment.paymentNotes != null &&
                                      payment.paymentNotes !== '' && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block', mt: 0.5 }}
                                        >
                                          {payment.paymentNotes}
                                        </Typography>
                                      )}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                      size="small"
                                      startIcon={<EditIcon />}
                                      onClick={() => handleEditPayment(payment.id)}
                                      disabled={!paymentMethodsEditable}
                                    >
                                      Editar
                                    </Button>
                                    <Button
                                      size="small"
                                      color="error"
                                      startIcon={<DeleteIcon />}
                                      onClick={() => handleRemovePayment(payment.id)}
                                      disabled={!paymentMethodsEditable}
                                    >
                                      Eliminar
                                    </Button>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    )}

                    {payments.length > 0 && (
                      <Box
                        sx={(theme) => ({
                          p: 2,
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? alpha(theme.palette.common.white, 0.06)
                              : theme.palette.grey[50],
                          border:
                            theme.palette.mode === 'dark'
                              ? `1px solid ${theme.palette.divider}`
                              : 'none',
                          borderRadius: 1,
                          mb: 2,
                        })}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Total de la venta:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(orderSummary.grandTotal)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Total pagado:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'primary.main' }}
                          >
                            {formatCurrency(getTotalPayments())}
                          </Typography>
                        </Box>
                        {getRemainingBalance() > 0 ? (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              Saldo pendiente:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: 'warning.main' }}
                            >
                              {formatCurrency(getRemainingBalance())}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              Cambio:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: 'success.main' }}
                            >
                              {formatCurrency(orderSummary.change)}
                            </Typography>
                          </Box>
                        )}
                        {orderSummary.paymentStatus === 'complete' && (
                          <SalesStepHint tone="success" sx={{ mb: 0, mt: 2 }}>
                            Pago completo
                          </SalesStepHint>
                        )}
                        {orderSummary.paymentStatus === 'partial' && (
                          <SalesStepHint tone="warning" sx={{ mb: 0, mt: 2 }}>
                            Pago parcial - Saldo pendiente: {formatCurrency(getRemainingBalance())}
                          </SalesStepHint>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>

              <Dialog
                open={showOtherMethodsDialog}
                onClose={handleCloseOtherMethods}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Seleccionar Método de Pago</DialogTitle>
                <DialogContent>
                  {otherPaymentMethods.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No hay otros métodos de pago disponibles.
                    </Typography>
                  ) : (
                    <List>
                      {otherPaymentMethods.map((method: PaymentMethod) => (
                        <ListItem key={method.id} disablePadding>
                          <ListItemButton onClick={() => handleOtherMethodSelect(method)}>
                            <ListItemIcon>
                              <PaymentIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={method.name}
                              secondary={method.description ?? method.code}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </DialogContent>
                <DialogActions>
                  <S.ModalCancelButton onClick={handleCloseOtherMethods}>
                    Cancelar
                  </S.ModalCancelButton>
                </DialogActions>
              </Dialog>

              <PaymentAmountDialog
                open={showPaymentAmountDialog}
                onClose={handleClosePaymentAmountDialog}
                method={
                  pendingPaymentMethod ??
                  (editingPaymentId != null
                    ? (payments.find((p) => p.id === editingPaymentId)?.method ?? null)
                    : null)
                }
                editingPayment={
                  editingPaymentId != null
                    ? (payments.find((p) => p.id === editingPaymentId) ?? null)
                    : null
                }
                remainingBalance={getRemainingBalance()}
                onAdd={handleAddPayment}
                onUpdate={handleUpdatePaymentAmount}
                formatCurrency={formatCurrency}
              />
            </CardContent>
          </Card>
        </Box>
      </S.LeftColumn>

      <S.RightColumn>
        <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CartIcon color="primary" />
              Resumen de Venta
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Sucursal
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mt: 0.25,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {state.selectedLocation?.name}
                </Typography>
                {cartHasRemoteItems(state.selectedProducts, state.selectedLocation?.id) && (
                  <Chip
                    label="Venta multi-sucursal"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                Piezas ({state.selectedProducts.length})
              </Typography>
              <Box>
                {state.selectedProducts.map((product) => (
                  <S.ProductCardBox key={product.warehouseInventoryProduct.id}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <ProductImagePreview
                        product={getProductImageData(
                          product.warehouseInventoryProduct.product as ProductImageData,
                        )}
                        alt={product.warehouseInventoryProduct.product.name}
                        size={60}
                        borderRadius={10}
                      />
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                            {product.warehouseInventoryProduct.product.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            Código: {product.warehouseInventoryProduct.productCodeUnique ?? 'N/A'}
                          </Typography>
                          {isRemoteCartItem(product, state.selectedLocation?.id) && (
                            <Box sx={{ mt: 0.5 }}>
                              <RemoteOriginChip
                                locationName={product.warehouseInventoryProduct.location?.name}
                              />
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'primary.main' }}
                          >
                            {formatCurrency(roundMoney(product.finalPrice))}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </S.ProductCardBox>
                ))}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal catálogo:
                </Typography>
                <Typography variant="body2">{formatCurrency(orderSummary.subtotal)}</Typography>
              </Box>
              {orderSummary.totalMarkup > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="success.main">
                    Incrementos:
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    +{formatCurrency(orderSummary.totalMarkup)}
                  </Typography>
                </Box>
              )}
              {orderSummary.totalDiscount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="error">
                    Descuentos:
                  </Typography>
                  <Typography variant="body2" color="error">
                    -{formatCurrency(orderSummary.totalDiscount)}
                  </Typography>
                </Box>
              )}
              {orderSummary.totalCharges > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Cargos adicionales:
                  </Typography>
                  <Typography variant="body2">
                    {formatCurrency(orderSummary.totalCharges)}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Total:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatCurrency(orderSummary.grandTotal)}
                </Typography>
              </Box>
            </Box>

            {payments.length > 0 && (
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  Información de Pago
                </Typography>
                <Box sx={{ space: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total pagado:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(getTotalPayments())}
                    </Typography>
                  </Box>
                  {getRemainingBalance() > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="warning.main">
                        Saldo pendiente:
                      </Typography>
                      <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                        {formatCurrency(getRemainingBalance())}
                      </Typography>
                    </Box>
                  )}
                  {orderSummary.change > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="success.main">
                        Cambio:
                      </Typography>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                        {formatCurrency(orderSummary.change)}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Pago parcial:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: state.paymentConfig.allowPartialPayment
                            ? 'success.main'
                            : 'text.secondary',
                        }}
                      >
                        {state.paymentConfig.allowPartialPayment ? 'Habilitado' : 'Deshabilitado'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        <S.NavigationBox>
          <S.BackButton fullWidth variant="outlined" onClick={previousStep}>
            Regresar al Paso 1
          </S.BackButton>

          <S.ContinueButton
            fullWidth
            variant="contained"
            onClick={handleNextStep}
            disabled={!canProceed}
          >
            Continuar al Paso 3
          </S.ContinueButton>
        </S.NavigationBox>

        {paymentMethodsVisible && !canProceed && (
          <Alert
            severity="error"
            sx={(theme) => ({
              mt: 2,
              backgroundColor: alpha(
                theme.palette.error.main,
                theme.palette.mode === 'dark' ? 0.14 : 0.08,
              ),
              color: theme.palette.error.light,
              border: `1px solid ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.4 : 0.35)}`,
              '& .MuiAlert-icon': {
                color: theme.palette.error.light,
              },
            })}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'error.light' }}>
              Pendientes:
            </Typography>
            <S.ValidationList>
              {payments.length === 0 && (
                <Typography component="li" variant="caption" sx={{ color: 'error.light' }}>
                  • Agregar al menos un método de pago
                </Typography>
              )}
              {payments.length > 0 && payments.some((p) => p.amount <= 0) && (
                <Typography component="li" variant="caption" sx={{ color: 'error.light' }}>
                  • Todos los pagos deben tener un monto mayor a cero
                </Typography>
              )}
              {payments.length > 0 &&
                !state.paymentConfig.allowPartialPayment &&
                getRemainingBalance() > 0 && (
                  <Typography component="li" variant="caption" sx={{ color: 'error.light' }}>
                    • El pago debe cubrir el total completo (saldo pendiente:{' '}
                    {formatCurrency(getRemainingBalance())})
                  </Typography>
                )}
            </S.ValidationList>
          </Alert>
        )}
      </S.RightColumn>
    </S.MainContainer>
  );
};

export default SalesStep2;
